import {
  AbsoluteAddressDropdown,
  AdminInput,
  AdminSpinner,
  Button,
  Div,
  ErrorToast,
  Modal,
  PropsModal,
  SuccessToast,
  api,
  lightTheme,
  parseGooglePlace,
  reportError,
} from '@vgrubs/components';
import { IncompleteLocation } from 'components/src/types/api/location.types';
import { LocationEntity } from 'components/src/types/location.types';
import { Dispatch, SetStateAction, useState } from 'react';
import PlacesAutocomplete, {
  geocodeByAddress,
} from 'react-places-autocomplete';
import { useParams } from 'react-router';

export const ChangeLocationAddress = ({
  modalIsOpen,
  closeModal,
  setLoading,
  setConfirmAddressModal,
  currentUnitNumber,
}: PropsModal & {
  modalIsOpen: boolean;
  setConfirmAddressModal: Dispatch<SetStateAction<IncompleteLocation | null>>;
  currentUnitNumber?: string | null;
}) => {
  const [address, setAddress] = useState('');
  const [placeId, setPlaceId] = useState('');

  const finished = () => {
    setAddress('');
    setPlaceId('');
    closeModal?.();
  };

  async function getLocationDetails() {
    if (!address || !placeId)
      return ErrorToast('Please choose an address from the dropdown.');

    try {
      setLoading?.(true);

      const { data } = await parseGooglePlace(placeId);

      // Google only knows the suite when it was typed into the search, so keep
      // the one already on the location when the place carries none. The field
      // is editable either way, and leaving it empty is what deletes it.
      setConfirmAddressModal({
        ...data.data,
        unitNumber: data.data.unitNumber ?? currentUnitNumber ?? null,
      });

      finished();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  return (
    <Modal
      isOpen={modalIsOpen}
      closeModal={closeModal}
      logo={false}
      zIndex={110}
      header={'Change Address'}
      overflow="visible"
      modalOverflow="visible"
    >
      <LocationSearchInput
        address={address}
        setAddress={setAddress}
        setPlaceId={setPlaceId}
      />

      <Button $mt="24" $w="100%" $selected onClick={getLocationDetails}>
        Confirm
      </Button>
      <Button $w="100%" $mt="16" $selected $gray onClick={finished}>
        Cancel
      </Button>
    </Modal>
  );
};

export const ChangeLocationAddressConfirm = ({
  modalIsOpen,
  setDetails,
  closeModal,
  setData,
  setLoading,
}: PropsModal & {
  modalIsOpen: IncompleteLocation | null;
  setDetails: Dispatch<SetStateAction<IncompleteLocation | null>>;
  setData: Dispatch<SetStateAction<LocationEntity | null>>;
}) => {
  const { id } = useParams() as { id: string };

  if (modalIsOpen === null) return;

  const {
    address,
    city,
    country,
    state,
    zipCode,
    coordinates,
    streetNumber,
    unitNumber,
  } = modalIsOpen;

  const handleInputChange = ({ target: { name, value } }) => {
    setDetails(
      (prevData) =>
        prevData && {
          ...prevData,
          [name]: value,
        }
    );
  };

  async function handleChangeAddress() {
    if (!address || !city || !country || !state || !zipCode || !streetNumber)
      return ErrorToast('Please fill in all information');

    try {
      setLoading?.(true);

      // The whole address is replaced, so an omitted unitNumber wipes the
      // stored suite - send it on every save, null when the field is empty.
      const trimmedUnitNumber = unitNumber?.trim() || null;

      const body = {
        address,
        city,
        country,
        state,
        zipCode,
        coordinates,
        streetNumber,
        unitNumber: trimmedUnitNumber,
      };

      const { data } = await api.put(`/v1/locations/${id}/address`, body);

      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          address: `${streetNumber} ${address}`,
          unitNumber: trimmedUnitNumber,
          city,
          country,
          state,
          zipCode,
        };
      });

      closeModal?.();

      SuccessToast(data.message);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      zIndex={110}
      header={'Change Address'}
    >
      <Div $flex $column $gap="24px">
        <AdminInput
          $textCenter
          placeholder="Enter Here"
          label="Address"
          value={address ?? ''}
          name="address"
          onChange={handleInputChange}
        />
        <Div $flex $alignCenter $gap="16px" $width="100%">
          <AdminInput
            $textCenter
            placeholder="Enter Here"
            label="Street Number"
            value={streetNumber ?? ''}
            name="streetNumber"
            onChange={handleInputChange}
          />

          <AdminInput
            $textCenter
            placeholder="Optional"
            label="Unit/Apt/Suite"
            value={unitNumber ?? ''}
            name="unitNumber"
            onChange={handleInputChange}
          />
        </Div>
        <Div $flex $alignCenter $gap="16px" $width="100%">
          <AdminInput
            $textCenter
            placeholder="Enter Here"
            label="City"
            value={city ?? ''}
            name="city"
            onChange={handleInputChange}
          />

          <AdminInput
            $textCenter
            placeholder="Enter Here"
            label="Country"
            value={country ?? ''}
            name="country"
            onChange={handleInputChange}
          />
        </Div>
        <Div $flex $alignCenter $gap="16px" $width="100%">
          <AdminInput
            $textCenter
            placeholder="Enter Here"
            label="State"
            value={state ?? ''}
            name="state"
            onChange={handleInputChange}
          />

          <AdminInput
            $textCenter
            placeholder="Enter Here"
            label="Zip Code"
            value={zipCode ?? ''}
            name="zipCode"
            onChange={handleInputChange}
          />
        </Div>
      </Div>

      <Button $mt="24" $w="100%" $selected onClick={handleChangeAddress}>
        Confirm
      </Button>
      <Button
        $w="100%"
        $mt="16"
        $selected
        $gray
        onClick={() => {
          closeModal?.();
        }}
      >
        Cancel
      </Button>
    </Modal>
  );
};

const LocationSearchInput = ({ address, setAddress, setPlaceId }) => {
  const handleChange = (address) => {
    setAddress(address);
  };

  const handleSelect = async (address) => {
    setAddress(address);

    try {
      const results = await geocodeByAddress(address);

      if (results.length > 0) {
        const placeId = results[0].place_id;
        setPlaceId(placeId);
      }
    } catch (error) {
      console.error('Error fetching geocode results:', error);
    }
  };

  return (
    <PlacesAutocomplete
      value={address}
      onChange={handleChange}
      onSelect={handleSelect}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
        return (
          <Div $relative $mb="24">
            <AdminInput
              $textCenter
              {...getInputProps({
                label: 'New Location Address',
                placeholder: 'Choose Address',
              })}
            />
            {suggestions.length ? (
              <AbsoluteAddressDropdown $box $p="8px">
                {loading && <AdminSpinner absolute small />}
                {suggestions.map((suggestion) => {
                  const style = suggestion.active
                    ? { color: lightTheme.primary, cursor: 'pointer' }
                    : { backgroundColor: '', cursor: 'pointer' };
                  return (
                    <Div
                      {...getSuggestionItemProps(suggestion, {
                        style,
                      })}
                      $p="8px"
                      key={suggestion.id}
                    >
                      <span>{suggestion.description}</span>
                    </Div>
                  );
                })}
              </AbsoluteAddressDropdown>
            ) : null}
          </Div>
        );
      }}
    </PlacesAutocomplete>
  );
};
